import { useEffect, useState, useRef, useCallback } from "react";
import { ShareExpiry } from "@/components/ShareExpiry";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { daDate, toDate, toISO, addDays } from "@/lib/date";
import { MPoint, MLabel, MCollapse, MCompactRows, MStat } from "@/components/le/MinutesUI";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { MinuteBlocksView, MinuteBlocksEditor } from "@/components/le/MinuteBlocks";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { UserSingleSelect } from "@/components/UserSingleSelect";
import { ResponsibleMultiSelect } from "@/components/ResponsibleMultiSelect";
import { DanishDatePicker } from "@/components/DanishDatePicker";
import { ActionFormDialog, ST_BADGE, AttachmentList } from "@/components/le/ActionUI";
import { FollowupCard, FollowupCardCompact, SinceSection } from "@/components/le/FollowupCard";
import { FindingCard, GoalCard } from "@/components/le/ObjectCards";
import { GOAL_STATUSES } from "@/pages/LeGoals";
import { AddInput, InputCard, inputSummary, INPUT_ICON, INPUT_LABEL, createMediaInput } from "@/components/le/InputUI";
import { AutoInputLines } from "@/components/le/AutoInputs";
import { SidePanel, LineList, LineRow } from "@/components/le/SidePanel";
import { OutcomePicker } from "@/components/le/OutcomePicker";

import { Loader2, Check, CloudUpload, AlertCircle, CheckCircle2, Share2, RotateCcw,
  Plus, ArrowLeft, X, Eye } from "lucide-react";

const HANDLING_OPTS = [["", "—"], ["behandlet", "Behandlet"], ["udsat", "Udsat"], ["ikke_relevant", "Ikke relevant"]];
const HANDLING_BADGE = { behandlet: "bg-[#E7EADF] text-[#5f6b4a]", udsat: "bg-[#EFE7DC] text-[#8A6D3B]", ikke_relevant: "bg-[#F0EDE8] text-stone-500" };
const BACKEND = process.env.REACT_APP_BACKEND_URL;
const MONTH_NAMES = ["Januar", "Februar", "Marts", "April", "Maj", "Juni", "Juli", "August", "September", "Oktober", "November", "December"];


function SnapFollowup({ f }) {
  const chips = [];
  if (f.status_to) chips.push(`Status: ${f.status_from || "—"} → ${f.status_to}`);
  if ("deadline_to" in f) chips.push(`Frist: ${daDate(f.deadline_from)} → ${daDate(f.deadline_to)}`);
  if ("responsible_to" in f) chips.push(`Ansvarlig: ${f.responsible_from || "—"} → ${f.responsible_to || "—"}`);
  return (
    <div className="rounded-lg bg-[#FBFAF8] border border-[#F0EDE8] px-3 py-2">
      <div className="text-[11px] text-stone-400">{daDate(f.at)} · {f.by_name || "—"}</div>
      {f.comment && <p className="text-sm text-stone-700 mt-0.5 whitespace-pre-wrap">{f.comment}</p>}
      {chips.length > 0 && <div className="flex flex-wrap gap-1.5 mt-1">{chips.map((c, i) => <span key={i} className="text-[10px] px-1.5 py-0.5 rounded bg-stone-100 text-stone-500">{c}</span>)}</div>}
    </div>
  );
}

function SnapActionCard({ a, showWork }) {
  return (
    <div className="bg-white rounded-2xl border border-[#EBE8E3] p-4">
      <div className="flex items-center gap-2 flex-wrap">
        <span className="font-mono text-xs text-stone-400">{a.action_no}</span>
        <span className={`text-[11px] px-2 py-0.5 rounded-full border ${ST_BADGE[a.status]}`}>{a.status}</span>
        {a.created_here && <span className="text-[11px] px-2 py-0.5 rounded-full bg-[#E7EADF] text-[#5f6b4a]">Ny på mødet</span>}
        {a.overdue && <span className="text-[11px] px-2 py-0.5 rounded-full bg-[#A83232] text-white">Overskredet</span>}
      </div>
      <p className="text-sm font-medium text-[#1A1919] mt-1.5 whitespace-pre-wrap">{a.description}</p>
      <p className="text-xs text-stone-500 mt-1">Ansvarlig: {a.responsible_name || "—"} · Frist: {daDate(a.deadline)}</p>
      {showWork && (a.meeting_followups || []).length > 0 && <div className="mt-2.5">
        <p className="text-[11px] font-semibold uppercase tracking-wide text-stone-400">Hvad blev gjort</p>
        <div className="mt-1.5 space-y-1.5">{a.meeting_followups.map((f, i) => <SnapFollowup key={i} f={f} />)}</div>
      </div>}
      {(a.files || []).length > 0 && <AttachmentList files={a.files} hrefFor={(fl) => `${BACKEND}/api/le/actions/${a.id}/files/${fl.id}`} />}
    </div>
  );
}

function SaveState({ state }) {
  if (state === "saving") return <span className="text-xs text-stone-400 flex items-center gap-1" data-testid="le-save-saving"><CloudUpload className="h-3.5 w-3.5" /> Gemmer…</span>;
  if (state === "saved") return <span className="text-xs text-[#5f6b4a] flex items-center gap-1" data-testid="le-save-saved"><Check className="h-3.5 w-3.5" /> Gemt</span>;
  if (state === "error") return <span className="text-xs text-[#A83232] flex items-center gap-1" data-testid="le-save-error"><AlertCircle className="h-3.5 w-3.5" /> Kunne ikke gemme</span>;
  if (state === "conflict") return <span className="text-xs text-[#A83232] flex items-center gap-1 font-medium" data-testid="le-save-conflict"><AlertCircle className="h-3.5 w-3.5" /> Ikke gemt — se besked</span>;
  return null;
}

function ConflictBanner({ message, onReload }) {
  if (!message) return null;
  return (
    <div role="alert" data-testid="le-conflict-banner" className="sticky top-2 z-30 rounded-xl border-2 border-[#A83232] bg-[#FBEDED] px-4 py-3 flex flex-wrap items-center gap-3 shadow-lg">
      <AlertCircle className="h-5 w-5 text-[#A83232] shrink-0" />
      <div className="flex-1 min-w-[200px]">
        <div className="text-sm font-semibold text-[#A83232]">Din seneste ændring er ikke gemt</div>
        <div className="text-sm text-[#1A1919]">{message}</div>
      </div>
      <Button size="sm" onClick={onReload} className="rounded-full bg-[#A83232] hover:bg-[#8a2828] text-white" data-testid="le-conflict-reload">Genindlæs siden</Button>
    </div>
  );
}

function ShareDialog({ meeting, onClose, onChange }) {
  const [token, setToken] = useState(meeting.share_token || null);
  const [expiry, setExpiry] = useState(meeting.share_expires_at || null);
  const [settings, setSettings] = useState(() => {
    const base = { participants: true, minutes: true, kpi: true, audits: true, reklamationer: true, attachments: true, actionplan: true, goals: true, f2_inputs: false };
    return { ...base, ...(meeting.share_settings || {}) };
  });
  const KEYS = [["participants", "Deltagere"], ["minutes", "Referat & beslutninger"], ["actionplan", "Handlingsplan"], ["goals", "Kvalitets- & miljømål"], ["kpi", "KPI-billeder"], ["audits", "Auditopsummeringer"], ["reklamationer", "Reklamationsstatistik"], ["attachments", "Noter & bilag"], ["f2_inputs", "LE-indstillinger, effektvurderinger & gentagelser"]];
  const url = token ? `${window.location.origin}/le-referat/${token}` : "";
  const create = async () => { try { const r = await api.post(`/le/meetings/${meeting.id}/share`); setToken(r.data.share_token); onChange?.(); toast.success("Delingslink oprettet"); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } };
  const regen = async () => { try { const r = await api.post(`/le/meetings/${meeting.id}/share/regenerate`); setToken(r.data.share_token); toast.success("Nyt link genereret"); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } };
  const del = async () => { try { await api.delete(`/le/meetings/${meeting.id}/share`); setToken(null); onChange?.(); toast.success("Deling deaktiveret"); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } };
  const toggle = async (k) => {
    if (k === "f2_inputs" && !settings[k] && !window.confirm("ADVARSEL: Du er ved at dele interne LE-indstillinger, afvisningsbegrundelser, effektvurderinger og gentagelsesadvarsler på den offentlige delingsside. Dette er internt materiale. Vil du fortsætte?")) return;
    const next = { ...settings, [k]: !settings[k] }; setSettings(next); try { await api.patch(`/le/meetings/${meeting.id}/share/settings`, { settings: next }); } catch (e) { toast.error("Kunne ikke gemme indstilling"); } };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="rounded-2xl max-w-lg" data-testid="le-share-dialog">
        <DialogHeader><DialogTitle>Del referat</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div>
            <p className="text-xs font-medium text-stone-500 mb-1.5">Medtag på delingssiden</p>
            <div className="grid grid-cols-2 gap-2">
              {KEYS.map(([k, l]) => (
                <label key={k} className={`flex items-center gap-2 text-sm rounded-lg border px-2.5 py-1.5 cursor-pointer ${k === "f2_inputs" ? "col-span-2 border-[#E9C9C3] bg-[#FDF8F6]" : "border-[#EBE8E3]"}`} data-testid={`le-share-toggle-${k}`}>
                  <input type="checkbox" checked={!!settings[k]} onChange={() => toggle(k)} className="accent-[#A83232]" /> {l}{k === "f2_inputs" && <span className="text-[10px] text-[#A83232] ml-auto">Internt – fra som standard</span>}
                </label>
              ))}
            </div>
            <p className="text-[11px] text-stone-400 mt-1.5">Interne forberedelsesnoter deles aldrig. Kildelinks virker kun for brugere med adgang.</p>
          </div>
          {token ? (
            <>
              <div><p className="text-xs font-medium text-stone-500 mb-1.5">Offentligt link</p>
                <div className="flex gap-2"><Input readOnly value={url} className="rounded-xl text-xs" data-testid="le-share-url" /><Button variant="outline" onClick={() => { navigator.clipboard.writeText(url); toast.success("Kopieret"); }} className="rounded-xl border-[#EBE8E3]">Kopiér</Button></div>
              </div>
              <ShareExpiry kind="le" docId={meeting.id} expiresAt={expiry} onChange={setExpiry} />
              <div className="flex gap-2">
                <a href={url} target="_blank" rel="noreferrer" className="text-sm text-[#806B61] hover:underline flex items-center gap-1"><Share2 className="h-3.5 w-3.5" /> Forhåndsvis</a>
                <button onClick={regen} className="text-sm text-stone-500 hover:underline ml-auto">Nyt link</button>
                <button onClick={del} className="text-sm text-[#A83232] hover:underline" data-testid="le-share-disable">Deaktivér</button>
              </div>
            </>
          ) : (
            <Button onClick={create} className="rounded-xl bg-[#A83232] hover:bg-[#8f2a2a] w-full" data-testid="le-share-create"><Share2 className="h-4 w-4 mr-1.5" /> Opret delingslink</Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function LinkActionDialog({ input, openActions, onClose, onLinked }) {
  const link = async (a) => {
    try {
      const li = [...(a.linked_inputs || []), { input_id: input.id, type: input.type, label: input.type, text: input.text || input.caption || "" }];
      await api.patch(`/le/actions/${a.id}`, { linked_inputs: li });
      toast.success(`Knyttet til ${a.action_no}`); onLinked?.(); onClose();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="rounded-2xl max-w-md" data-testid="le-link-action-dialog">
        <DialogHeader><DialogTitle>Knyt input til en handling</DialogTitle></DialogHeader>
        <div className="max-h-72 overflow-y-auto space-y-2">
          {openActions.length === 0 && <p className="text-sm text-stone-500">Ingen åbne handlinger.</p>}
          {openActions.map((a) => (
            <button key={a.id} onClick={() => link(a)} data-testid={`le-link-target-${a.id}`} className="w-full text-left rounded-xl border border-[#EBE8E3] p-3 hover:border-[#A83232]">
              <span className="font-mono text-xs text-stone-400">{a.action_no}</span>
              <p className="text-sm text-[#1A1919]">{a.description}</p>
            </button>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function GoalEvalBox({ goal, meeting, onSaved }) {
  const [text, setText] = useState("");
  const [status, setStatus] = useState(goal.status);
  const [outcome, setOutcome] = useState(null);
  const [busy, setBusy] = useState(false);
  const save = async () => {
    if (!text.trim() && status === goal.status) { toast.error("Skriv en evaluering eller skift status"); return; }
    setBusy(true);
    try { await api.post(`/le/goals/${goal.id}/evaluation`, { text, new_status: status !== goal.status ? status : undefined, outcome: status === "Lukket" ? outcome : undefined, meeting_id: meeting.id }); toast.success("Evaluering gemt"); setText(""); onSaved?.(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    setBusy(false);
  };
  return (
    <div className="mt-3 pt-3 border-t border-[#F0EDE8]">
      <div className="flex items-center gap-2 flex-wrap mb-2">
        <span className="text-xs text-stone-500">Status:</span>
        <div className="inline-flex rounded-lg border border-[#EBE8E3] p-0.5 flex-wrap">
          {GOAL_STATUSES.map((s) => <button key={s} type="button" onClick={() => setStatus(s)} data-testid={`le-goalreview-status-${goal.id}-${s}`} className={`px-2 py-1 rounded text-xs ${status === s ? "bg-[#A83232] text-white" : "text-stone-600"}`}>{s}</button>)}
        </div>
        {status === "Lukket" && goal.status !== "Lukket" && <OutcomePicker value={outcome} onChange={setOutcome} testid={`le-goalreview-outcome-${goal.id}`} />}
      </div>
      <Textarea value={text} onChange={(e) => setText(e.target.value)} className="rounded-xl min-h-[56px] text-sm" placeholder="Evaluering til dette møde…" data-testid={`le-goalreview-text-${goal.id}`} />
      <div className="flex justify-end mt-2"><Button size="sm" onClick={save} disabled={busy} className="rounded-lg bg-[#A83232] hover:bg-[#8f2a2a]" data-testid={`le-goalreview-save-${goal.id}`}>Gem evaluering</Button></div>
    </div>
  );
}

function GoalsReview({ meeting, users, isFinal, snapshot, onActionsChanged, allActions }) {
  const [goals, setGoals] = useState(null);
  const [actionGoal, setActionGoal] = useState(null);
  const load = useCallback(async () => { try { const r = await api.get("/le/goals", { params: { view: "open" } }); setGoals(r.data); } catch (_) { /* noop */ } }, []);
  useEffect(() => { if (!isFinal) load(); }, [isFinal, load]);

  if (isFinal) {
    const list = snapshot?.goals || [];
    const gacts = (snapshot?.actions?.open || []).concat(snapshot?.actions?.closed || []);
    if (list.length === 0) return <div className="bg-white rounded-2xl border border-[#EBE8E3] p-8 text-center text-stone-400">Ingen mål registreret på mødet.</div>;
    return <div className="space-y-3" data-testid="le-goals-review-final">{list.map((g, i) => (
      <GoalCard key={g.id || i} goal={g} readonly actions={gacts.filter((a) => a.goal_id === g.id)} />
    ))}</div>;
  }

  if (goals === null) return <div className="text-sm text-stone-400 flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" /> Indlæser mål…</div>;
  if (goals.length === 0) return <div className="bg-white rounded-2xl border border-[#EBE8E3] p-8 text-center text-stone-400">Ingen aktive mål. Opret mål under «Kvalitets- & miljømål».</div>;
  const gs = { open: goals.filter((g) => g.status === "Åben").length, prog: goals.filter((g) => g.status === "I gang").length, overdue: goals.filter((g) => g.overdue).length, stale: goals.filter((g) => !g.latest_evaluation).length };

  return (
    <div className="space-y-3" data-testid="le-goals-review">
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2" data-testid="le-goal-status-cards">
        {[["Aktive mål", goals.length, "text-[#1A1919]"], ["I gang", gs.prog, "text-[#806B61]"], ["Overskredne", gs.overdue, gs.overdue ? "text-[#A83232]" : "text-[#1A1919]"], ["Uden evaluering", gs.stale, gs.stale ? "text-[#8A6D3B]" : "text-[#1A1919]"]].map(([l, v, t]) => (
          <div key={l} className="rounded-xl border border-[#EBE8E3] bg-white px-3.5 py-3"><p className="text-[11px] text-stone-500">{l}</p><p className={`text-2xl font-semibold mt-0.5 ${t}`}>{v}</p></div>
        ))}
      </div>
      {goals.map((g) => (
        <GoalCard key={g.id} goal={g} actions={(allActions || []).filter((a) => a.goal_id === g.id)}
          badge={<Button size="sm" variant="outline" onClick={() => setActionGoal(g)} className="rounded-lg border-[#EBE8E3] h-7 px-2 text-xs ml-auto" data-testid={`le-goalreview-action-${g.id}`}><Plus className="h-3.5 w-3.5 mr-1" /> Handling</Button>}>
          <GoalEvalBox goal={g} meeting={meeting} onSaved={load} />
        </GoalCard>
      ))}
      {actionGoal && <ActionFormDialog open onOpenChange={() => setActionGoal(null)} users={users} meeting={meeting} goal={actionGoal} onSaved={() => { setActionGoal(null); load(); onActionsChanged?.(); }} />}
    </div>
  );
}

function ClosedSinceCard({ a }) {
  return (
    <div className="bg-white rounded-2xl border border-[#E1E7D6] p-4" data-testid={`le-closed-since-${a.action_no}`}>
      <div className="flex items-center gap-2 flex-wrap">
        <span className="font-mono text-xs text-stone-400">{a.action_no}</span>
        <span className={`text-[11px] px-2 py-0.5 rounded-full border ${ST_BADGE[a.status]}`}>{a.status}</span>
        <span className="text-[11px] text-stone-400">Lukket {a.closed_at ? daDate(a.closed_at) : ""}{a.closed_by_name ? ` · ${a.closed_by_name}` : ""}</span>
      </div>
      <p className="text-sm font-medium text-[#1A1919] mt-1.5 whitespace-pre-wrap">{a.description}</p>
      <p className="text-xs text-stone-500 mt-1">Ansvarlig: {a.responsible_name || "—"}</p>
      {a.closing_comment && <p className="text-sm mt-2 rounded-lg bg-[#F6F8F1] border border-[#D3D8C6] px-3 py-2 text-stone-700 whitespace-pre-wrap"><span className="text-[#5f6b4a] font-medium">Løsning: </span>{a.closing_comment}</p>}
      {(a.files || []).length > 0 && <AttachmentList files={a.files} hrefFor={(fl) => `${BACKEND}/api/le/actions/${a.id}/files/${fl.id}`} />}
    </div>
  );
}

function ActionplanFollowup({ meeting, users, item, onActionsChanged }) {
  const [data, setData] = useState(null);
  const load = useCallback(async () => {
    try { const r = await api.get(`/le/meetings/${meeting.id}/followup-actions`); setData(r.data); } catch (_) { /* noop */ }
  }, [meeting.id]);
  useEffect(() => { load(); }, [load]);
  const since = data?.since || { items: [], quiet: [] };
  const refresh = () => { load(); onActionsChanged?.(); };
  return (
    <div className="space-y-4" data-testid={`le-agenda-content-${item.id}`}>
      <p className="text-sm text-stone-500">Alt der er sket siden forrige færdige møde{since.cutoff ? ` (${daDate(since.cutoff)})` : ""} — også uden for LE.</p>
      {!data ? <div className="text-sm text-stone-400 px-1">Indlæser…</div> : <SinceSection since={since} users={users} meeting={meeting} onChanged={refresh} />}
    </div>
  );
}

export default function LeMeeting() {
  const { id } = useParams();
  const nav = useNavigate();
  const [meeting, setMeeting] = useState(null);
  const [users, setUsers] = useState([]);
  const [allActions, setAllActions] = useState([]);
  const openActions = allActions.filter((a) => a.status !== "Lukket");
  const [auto, setAuto] = useState(null);
  const [saveState, setSaveState] = useState("idle");
  const [conflict, setConflict] = useState(null);
  const meetingRef = useRef(null);
  useEffect(() => { meetingRef.current = meeting; }, [meeting]);
  const [shareOpen, setShareOpen] = useState(false);
  const [actionDlg, setActionDlg] = useState(null); // {agendaItem} | {input,agendaItem}
  const [linkDlg, setLinkDlg] = useState(null);
  const timer = useRef(null);
  const loc = useLocation();
  const [guestInput, setGuestInput] = useState("");
  const isFinal = meeting?.status === "final";

  const loadActions = useCallback(async () => {
    try { const r = await api.get("/le/actions", { params: { view: "all" } }); setAllActions(r.data); } catch (_) {}
  }, []);
  const [findings, setFindings] = useState([]);
  const [findingsDue, setFindingsDue] = useState([]);
  const loadFindings = useCallback(async () => {
    try {
      const [f, d] = await Promise.all([api.get("/le/findings/cards"), api.get(`/le/meetings/${id}/followup-actions`)]);
      setFindings(f.data); setFindingsDue(d.data.findings_due || []);
    } catch (_) {}
  }, [id]);
  useEffect(() => { if (meeting && !isFinal) loadFindings(); }, [meeting?.id, isFinal, loadFindings]); // eslint-disable-line react-hooks/exhaustive-deps
  const loadAuto = useCallback(async () => {
    try { const r = await api.get(`/le/meetings/${id}/auto-inputs`); setAuto(r.data); } catch (_) {}
  }, [id]);

  useEffect(() => {
    (async () => {
      try {
        const [m, u] = await Promise.all([api.get(`/le/meetings/${id}`), api.get("/users")]);
        setMeeting(m.data); setUsers(u.data);
        loadActions();
        if (m.data.status === "final" && m.data.snapshot?.f2) setAuto(m.data.snapshot.f2); else loadAuto();
      } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); nav("/ledelse"); }
    })();
  }, [id]); // eslint-disable-line

  useEffect(() => {
    if (meeting?.status === "final" && loc.state?.openShare) setShareOpen(true);
  }, [meeting]); // eslint-disable-line

  const onSaveError = useCallback((e) => {
    const msg = formatApiError(e.response?.data?.detail);
    if (e.response?.status === 409) { setSaveState("conflict"); setConflict(msg); }
    else { setSaveState("error"); toast.error(msg); }
  }, []);

  const doSave = useCallback(async (m) => {
    setSaveState("saving");
    try {
      const r = await api.patch(`/le/meetings/${id}`, {
        title: m.title, date: m.date, location: m.location,
        participant_ids: m.participant_ids, minute_taker_id: m.minute_taker_id });
      setSaveState("saved");
      setMeeting((prev) => ({ ...r.data, agenda: prev?.agenda || r.data.agenda }));
    } catch (e) { onSaveError(e); }
  }, [id, onSaveError]);

  const schedule = useCallback((m) => {
    setMeeting(m);
    if (isFinal) return;
    setSaveState("saving");
    clearTimeout(timer.current);
    timer.current = setTimeout(() => doSave(m), 700);
  }, [doSave, isFinal]);

  // Gem pr. dagsordenspunkt med versionstjek (trin 0) – ét gem ad gangen pr. punkt, så egne hurtige ændringer aldrig giver selv-409
  const itemTimers = useRef({});
  const itemPending = useRef({});
  const itemVersion = useRef({});
  const itemInFlight = useRef({});
  const saveItem = useCallback(async (itemId) => {
    if (itemInFlight.current[itemId]) return;
    const patch = itemPending.current[itemId]; delete itemPending.current[itemId];
    if (!patch) return;
    const cur = meetingRef.current?.agenda?.find((it) => it.id === itemId);
    const version = itemVersion.current[itemId] ?? cur?.version ?? 0;
    itemInFlight.current[itemId] = true;
    try {
      const r = await api.patch(`/le/meetings/${id}/agenda/${itemId}`, { version, patch });
      itemVersion.current[itemId] = r.data.version;
      setMeeting((prev) => ({ ...prev, agenda: prev.agenda.map((it) => it.id === itemId ? { ...it, version: r.data.version, updated_at: r.data.updated_at, updated_by_name: r.data.updated_by_name } : it) }));
      setSaveState((s) => (s === "conflict" ? s : "saved"));
    } catch (e) { onSaveError(e); }
    finally {
      itemInFlight.current[itemId] = false;
      if (itemPending.current[itemId]) saveItem(itemId);
    }
  }, [id, onSaveError]);

  const updateItem = (itemId, patch) => {
    setMeeting((m) => ({ ...m, agenda: m.agenda.map((it) => it.id === itemId ? { ...it, ...patch } : it) }));
    if (isFinal) return;
    setSaveState("saving");
    itemPending.current[itemId] = { ...(itemPending.current[itemId] || {}), ...patch };
    clearTimeout(itemTimers.current[itemId]);
    itemTimers.current[itemId] = setTimeout(() => saveItem(itemId), 700);
  };
  const updateHeader = (patch) => schedule({ ...meeting, ...patch });

  const addInput = (itemId, inp) => {
    const it = meeting.agenda.find((x) => x.id === itemId);
    updateItem(itemId, { inputs: [...(it.inputs || []), { ...inp, include: true }] });
  };
  const dropFile = async (itemId, file, type) => {
    try { addInput(itemId, await createMediaInput(id, file, type)); toast.success(type === "kpi" ? "KPI-billede tilføjet som grundlag" : "Bilag tilføjet som grundlag"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const removeInput = (itemId, inp) => {
    const it = meeting.agenda.find((x) => x.id === itemId);
    updateItem(itemId, { inputs: (it.inputs || []).filter((x) => x.id !== inp.id) });
  };
  const replaceInput = (itemId, inp) => {
    const it = meeting.agenda.find((x) => x.id === itemId);
    updateItem(itemId, { inputs: (it.inputs || []).map((x) => x.id === inp.id ? inp : x) });
  };
  const refreshInputData = async (inp) => {
    if (inp.type === "audit") {
      const r = await api.get("/le/sources/audits");
      const by = Object.fromEntries(r.data.map((a) => [a.id, a]));
      const audits = (inp.audit_ids || []).map((aid) => by[aid]).filter(Boolean)
        .map((a) => ({ id: a.id, audit_no: a.audit_no, title: a.title, date: a.date, areas: a.areas, counts: a.counts, behandlet_le: a.behandlet_le }));
      return { ...inp, audits, fetched_at: new Date().toISOString() };
    }
    if (inp.type === "reklamation") {
      const s = inp.stats || {};
      const params = { period: s.period, meeting_date: meeting.date, rtype: s.rtype };
      if (s.period === "custom") { params.date_from = s.from; params.date_to = s.to; }
      const r = await api.get("/le/sources/reklamationer", { params });
      return { ...inp, stats: { ...r.data, selected_case_ids: s.selected_case_ids || [] }, fetched_at: r.data.fetched_at };
    }
    return inp;
  };
  const refreshInput = async (itemId, inp) => {
    try { replaceInput(itemId, await refreshInputData(inp)); toast.success("Input opdateret"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  // --- børn (nestede punkter under fx "Punkter fra ledelsens evaluering") ---
  const updateChild = (itemId, childId, patch) => {
    const it = (meetingRef.current || meeting).agenda.find((x) => x.id === itemId);
    const children = (it.children || []).map((c) => c.id === childId ? { ...c, ...patch } : c);
    updateItem(itemId, { children });
  };
  const addChildInput = (itemId, childId, inp) => {
    const it = meeting.agenda.find((x) => x.id === itemId);
    const ch = (it.children || []).find((c) => c.id === childId);
    updateChild(itemId, childId, { inputs: [...(ch.inputs || []), { ...inp, include: true }] });
  };
  const removeChildInput = (itemId, childId, inp) => {
    const it = meeting.agenda.find((x) => x.id === itemId);
    const ch = (it.children || []).find((c) => c.id === childId);
    updateChild(itemId, childId, { inputs: (ch.inputs || []).filter((x) => x.id !== inp.id) });
  };
  const refreshChildInput = async (itemId, childId, inp) => {
    const it = meeting.agenda.find((x) => x.id === itemId);
    const ch = (it.children || []).find((c) => c.id === childId);
    try {
      const nd = await refreshInputData(inp);
      updateChild(itemId, childId, { inputs: (ch.inputs || []).map((x) => x.id === inp.id ? nd : x) });
      toast.success("Input opdateret");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const addExtraItem = async () => {
    try {
      const r = await api.post(`/le/meetings/${id}/agenda`, { title: "Ekstra punkt" });
      setMeeting((prev) => ({ ...prev, agenda: [...prev.agenda, r.data] }));
    } catch (e) { onSaveError(e); }
  };
  const removeItem = async (itemId) => {
    if (!window.confirm("Fjern dette dagsordenspunkt?")) return;
    try {
      await api.delete(`/le/meetings/${id}/agenda/${itemId}`);
      setMeeting((prev) => ({ ...prev, agenda: prev.agenda.filter((it) => it.id !== itemId) }));
    } catch (e) { onSaveError(e); }
  };

  const finalize = async () => {
    if (!window.confirm("Færdiggør referatet? Der gemmes et øjebliksbillede. Du kan senere redigere som ny version.")) return;
    if (!isFinal) await doSave(meeting);
    try { const r = await api.post(`/le/meetings/${id}/finalize`); setMeeting(r.data); if (r.data.snapshot?.f2) setAuto(r.data.snapshot.f2); toast.success("Referat færdiggjort"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const reopen = async () => {
    if (!window.confirm("Genåbn referatet som ny version til redigering?")) return;
    try { const r = await api.post(`/le/meetings/${id}/reopen`); setMeeting(r.data); loadAuto(); toast.success("Genåbnet som kladde"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  if (!meeting) return <div className="flex items-center gap-2 text-stone-400"><Loader2 className="h-4 w-4 animate-spin" /> Indlæser…</div>;
  const agenda = meeting.agenda || [];
  const jumpTo = (itId) => { setTimeout(() => document.getElementById(`le-agenda-item-${itId}`)?.scrollIntoView({ behavior: "smooth", block: "start" }), 50); };

  const treated = agenda.filter((it) => it.handling_status === "behandlet" || (it.children || []).length > 0 && it.children.every((c) => c.handling_status)).length;
  const newHere = openActions.filter((a) => a.origin_meeting_id === meeting.id).length;
  const overdueCount = openActions.filter((a) => a.overdue).length + (findingsDue || []).filter((f) => f.overdue && f.status !== "Lukket").length;
  const untreated = agenda.filter((it) => !it.special && !it.handling_status && !(it.children || []).length).map((it, _, arr) => agenda.indexOf(it) + 1);
  const missingEffect = !isFinal && auto ? ((auto.approved_reklamationer || []).filter((r) => !r.review).length) : 0;
  const missing = [missingEffect ? `${missingEffect} ${missingEffect === 1 ? "reklamation" : "reklamationer"} uden effektvurdering (pkt. ${agenda.findIndex((x) => x.key === "uoenskede_haendelser") + 1})` : null, untreated.length ? `pkt. ${untreated.join(", ")} ikke markeret behandlet` : null].filter(Boolean);
  const pointRed = (it, i) => (it.special === "actionplan_next" && overdueCount > 0) || (it.key === "uoenskede_haendelser" && findings.some((f) => f.overdue && f.status !== "Lukket"));
  const pointGreen = (it) => it.handling_status === "behandlet" || ((it.children || []).length > 0 && it.children.every((c) => c.handling_status === "behandlet"));
  const lastSaved = saveState === "saved" ? new Date().toTimeString().slice(0, 5) : null;

  return (
    <div className="-mx-4 sm:-mx-6 -mt-4 min-h-screen" style={{ background: "#FBFAF8" }} data-testid="le-meeting-page">
      {/* Topbar */}
      <div className="sticky top-0 z-30 bg-white/95 backdrop-blur border-b border-[#EBE8E3] px-4 sm:px-8 py-3">
        <div className="max-w-5xl mx-auto">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div className="flex items-center gap-3 flex-wrap text-sm text-stone-500">
              <button onClick={() => nav("/ledelse")} className="text-stone-500 hover:text-stone-800 flex items-center gap-1" data-testid="le-back"><ArrowLeft className="h-4 w-4" /></button>
              <span className={`text-[11px] font-medium px-2.5 py-1 rounded-full ${isFinal ? "bg-[#E7EADF] text-[#5f6b4a]" : "bg-[#EFE7DC] text-[#8A6D3B]"}`} data-testid="le-status-badge">{isFinal ? `Færdigt · v${meeting.version}` : "Kladde"}</span>
              <span>LE {daDate(meeting.date)}{!isFinal && saveState === "saved" ? ` · Gemt automatisk ${lastSaved}` : ""}</span>
              <SaveState state={saveState === "saved" ? "idle" : saveState} />
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => nav(`/ledelse/moede/${id}/referat`)} className="rounded-xl border-[#EBE8E3] bg-white" data-testid="le-preview-minutes-btn"><Eye className="h-4 w-4 mr-1.5" /> Vis som referat</Button>
              {isFinal ? (<>
                <Button variant="outline" onClick={reopen} className="rounded-xl border-[#EBE8E3] bg-white" data-testid="le-reopen-btn"><RotateCcw className="h-4 w-4 mr-1.5" /> Redigér (ny version)</Button>
                <Button onClick={() => setShareOpen(true)} className="rounded-xl bg-[#A83232] hover:bg-[#8f2a2a]" data-testid="le-share-btn"><Share2 className="h-4 w-4 mr-1.5" /> Del referat</Button>
              </>) : <Button onClick={finalize} className="rounded-xl bg-[#A83232] hover:bg-[#8f2a2a]" data-testid="le-finalize-btn"><CheckCircle2 className="h-4 w-4 mr-1.5" /> Færdiggør referat</Button>}
            </div>
          </div>
          <div className="flex items-center gap-2 mt-3 overflow-x-auto pb-0.5" data-testid="le-point-nav">
            <button onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })} className="shrink-0 text-xs font-medium px-3.5 py-1.5 rounded-full bg-[#1A1919] text-white">Overblik</button>
            {agenda.map((it, i) => (
              <button key={it.id} onClick={() => jumpTo(it.id)} data-testid={`le-agenda-nav-${it.id}`} className={`shrink-0 h-8 min-w-[2rem] px-2.5 text-xs font-semibold rounded-full inline-flex items-center gap-1 ${pointGreen(it) ? "bg-[#E7EADF] text-[#5f6b4a]" : "bg-[#F3F1EC] text-stone-600 hover:bg-[#EBE8E3]"}`}>{i + 1}{pointRed(it, i) && <span className="h-1.5 w-1.5 rounded-full bg-[#A83232]" />}</button>
            ))}
            <span className="text-[11px] text-stone-400 ml-2 shrink-0">grøn = behandlet</span>
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 sm:px-8 pb-24">
        <ConflictBanner message={conflict} onReload={() => window.location.reload()} />
        <section className="pt-10">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#A83232]">Ledelsens evaluering · Referat</p>
          {isFinal ? <h1 className="text-4xl md:text-5xl font-semibold tracking-tight mt-3 text-[#1A1919]">{meeting.title}</h1>
            : <input value={meeting.title} onChange={(e) => updateHeader({ title: e.target.value })} className="w-full bg-transparent text-4xl md:text-5xl font-semibold tracking-tight mt-3 text-[#1A1919] outline-none border-b border-transparent focus:border-[#EBE8E3]" data-testid="le-meeting-title-edit" />}
          <div className="bg-white rounded-2xl border border-[#EBE8E3] p-6 md:p-8 mt-8" data-testid="le-meta-card">
            <div className="grid sm:grid-cols-3 gap-5">
              <div><p className="text-xs font-semibold uppercase tracking-[0.1em] text-stone-400 mb-2">Dato</p>{isFinal ? <p className="text-base">{daDate(meeting.date)}</p> : <DanishDatePicker value={meeting.date} onChange={(d) => updateHeader({ date: d })} testid="le-meeting-date-edit" allowClosed />}</div>
              <div><p className="text-xs font-semibold uppercase tracking-[0.1em] text-stone-400 mb-2">Mødested</p>{isFinal ? <p className="text-base">{meeting.location || "—"}</p> : <Input value={meeting.location || ""} onChange={(e) => updateHeader({ location: e.target.value })} className="rounded-xl h-10" data-testid="le-meeting-location-edit" />}</div>
              <div><p className="text-xs font-semibold uppercase tracking-[0.1em] text-stone-400 mb-2">Referent</p>{isFinal ? <p className="text-base">{meeting.minute_taker_name || "—"}</p> : <UserSingleSelect users={users} value={meeting.minute_taker_id} onChange={(v) => updateHeader({ minute_taker_id: v })} testid="le-meeting-minutetaker-edit" className="h-10" placeholder="Vælg referent" />}</div>
            </div>
            <div className="mt-5">
              <p className="text-xs font-semibold uppercase tracking-[0.1em] text-stone-400 mb-2">Deltagere</p>
              {isFinal ? <p className="text-base">{(meeting.participant_names || []).join(", ") || "—"}</p> : (
                <div className="rounded-xl border border-[#EBE8E3] p-2.5">
                  <ResponsibleMultiSelect users={users} value={meeting.participant_ids || []} onChange={(v) => updateHeader({ participant_ids: v })} testid="le-meeting-participants-edit" className="h-10" />
                  <div className="flex flex-wrap items-center gap-1.5 mt-2">
                    {(meeting.guests || []).map((g) => <span key={g} className="inline-flex items-center gap-1 text-sm rounded-full bg-[#F3F1EC] text-stone-700 px-2.5 py-1" data-testid={`le-guest-chip-${g}`}>{g}<button onClick={() => updateHeader({ guests: (meeting.guests || []).filter((x) => x !== g) })} className="hover:text-[#A83232]"><X className="h-3 w-3" /></button></span>)}
                    <input value={guestInput} onChange={(e) => setGuestInput(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); const g = guestInput.trim(); if (g && !(meeting.guests || []).includes(g)) updateHeader({ guests: [...(meeting.guests || []), g] }); setGuestInput(""); } }} placeholder="Tilføj gæst (ikke-bruger) og tryk Enter…" className="flex-1 min-w-[12rem] text-sm bg-transparent outline-none px-1 py-1 text-stone-700 placeholder:text-stone-400" data-testid="le-guest-input-edit" />
                  </div>
                </div>
              )}
            </div>
          </div>
          <div className="grid grid-cols-3 gap-3 mt-4" data-testid="le-stats">
            <MStat value={`${treated} / ${agenda.length}`} label="punkter behandlet" testid="le-stat-treated" />
            <MStat value={newHere} label={newHere === 1 ? "ny handling" : "nye handlinger"} testid="le-stat-new" />
            <MStat value={overdueCount} label="overskredet" red testid="le-stat-overdue" />
          </div>
          {!isFinal && missing.length > 0 && <div className="mt-3 rounded-xl px-4 py-3 text-sm" style={{ background: "#FBF3DC", color: "#8A6D3B" }} data-testid="le-missing-banner">Mangler før færdiggørelse: {missing.join(" · ")}</div>}
        </section>

        {agenda.map((it, i) => (
          <div key={it.id} id={`le-agenda-item-${it.id}`} className="scroll-mt-28"
            onPaste={(e) => { if (isFinal || e.target.closest("[role=dialog]")) return; const f = [...(e.clipboardData?.items || [])].find((x) => x.type.startsWith("image/"))?.getAsFile(); if (f) { e.preventDefault(); dropFile(it.id, f, "kpi"); } }}
            onDragOver={(e) => { if (!isFinal && e.dataTransfer.types.includes("Files")) e.preventDefault(); }}
            onDrop={(e) => { if (isFinal) return; const f = e.dataTransfer.files?.[0]; if (f && !e.target.closest("[role=dialog]")) { e.preventDefault(); dropFile(it.id, f, "note"); } }}>
            <AgendaContent key={it.id} item={it} pointNo={i + 1} meeting={meeting} users={users} openActions={openActions} isFinal={isFinal} snapshot={meeting.snapshot}
              onUpdate={(patch) => updateItem(it.id, patch)} onRemoveItem={() => removeItem(it.id)}
              onAddInput={(inp) => addInput(it.id, inp)} onRemoveInput={(inp) => removeInput(it.id, inp)}
              onRefreshInput={(inp) => refreshInput(it.id, inp)}
              onUpdateChild={(cid, patch) => updateChild(it.id, cid, patch)}
              onAddChildInput={(cid, inp) => addChildInput(it.id, cid, inp)}
              onRemoveChildInput={(cid, inp) => removeChildInput(it.id, cid, inp)}
              onRefreshChildInput={(cid, inp) => refreshChildInput(it.id, cid, inp)}
              onCreateAction={(inp, ov) => setActionDlg({ agendaItem: ov || it, input: inp || null })}
              onLinkAction={(inp) => setLinkDlg({ input: inp })}
              onActionsChanged={loadActions}
              autoSlot={it.key === "uoenskede_haendelser" ? <AutoInputLines meeting={meeting} users={users} isFinal={isFinal} data={auto} onReload={loadAuto} onActionsChanged={loadActions} /> : null}
              findings={isFinal ? (meeting.snapshot?.actions?.findings || []) : findings} findingsDue={isFinal ? (meeting.snapshot?.actions?.findings_due || []) : findingsDue} onFindingsChanged={loadFindings} />
          </div>
        ))}
        {!isFinal && <button onClick={addExtraItem} data-testid="le-add-agenda-item" className="mt-10 w-full rounded-2xl border border-dashed border-[#EBE8E3] px-4 py-3 text-sm text-stone-500 hover:bg-[#F3F1EC] flex items-center gap-2 justify-center"><Plus className="h-4 w-4" /> Tilføj ekstra punkt</button>}
      </div>

      {shareOpen && <ShareDialog meeting={meeting} onClose={() => setShareOpen(false)} onChange={() => api.get(`/le/meetings/${id}`).then((r) => setMeeting(r.data))} />}
      {actionDlg && <ActionFormDialog open onOpenChange={() => setActionDlg(null)} users={users} meeting={meeting} agendaItem={actionDlg.agendaItem} input={actionDlg.input} onSaved={() => loadActions()} />}
      {linkDlg && <LinkActionDialog input={linkDlg.input} openActions={openActions} onClose={() => setLinkDlg(null)} onLinked={loadActions} />}
    </div>
  );
}

function HandlingSeg({ value, onChange, note, onNote, readonly, testid }) {
  const opts = HANDLING_OPTS.slice(1);
  if (readonly) return value ? <span className={`text-[11px] px-2.5 py-1 rounded-full ${HANDLING_BADGE[value]}`}>{opts.find((o) => o[0] === value)?.[1]}{note && value !== "behandlet" ? ` · ${note}` : ""}</span> : null;
  return (
    <div className="flex items-center gap-2 flex-wrap">
      <div className="inline-flex rounded-xl border border-[#EBE8E3] bg-white p-0.5" data-testid={testid}>
        {opts.map(([v, l]) => <button key={v} type="button" data-testid={`${testid}-${v}`} onClick={() => onChange({ handling_status: value === v ? null : v })} className={`px-3 py-1.5 rounded-lg text-sm transition-colors ${value === v ? "bg-[#1A1919] text-white" : "text-stone-600 hover:bg-[#F3F1EC]"}`}>{l}</button>)}
      </div>
      {(value === "udsat" || value === "ikke_relevant") && <Input value={note || ""} onChange={(e) => onNote(e.target.value)} placeholder="Kort bemærkning…" className="rounded-lg h-9 text-sm max-w-xs bg-white" data-testid="le-handling-note" />}
    </div>
  );
}

function Grundlag({ inputs, isFinal, meeting, onAdd, onRemove, onRefresh, onLinkAction, onCreateAction, findings, onFindingsChanged, testid, autoSlot }) {
  const list = inputs || [];
  const [openInp, setOpenInp] = useState(null);
  const cur = list.find((x) => x.id === openInp);
  return (
    <div className="mt-6" data-testid={testid} data-empty={list.length === 0}>
      {!isFinal && <div className="flex items-center gap-4 text-sm font-medium text-stone-700"><button type="button" onClick={() => onCreateAction(null)} className="hover:text-stone-900" data-testid="le-create-action-from-item">+ Handling</button><AddInput meetingId={meeting.id} meetingDate={meeting.date} onAdd={onAdd} trigger={(open) => <button type="button" onClick={open} className="hover:text-stone-900" data-testid="le-add-input-btn">+ Tilføj grundlag</button>} /></div>}
      {(list.length > 0 || autoSlot) && <div className="mt-5">
        {list.length > 0 && <MLabel className="mb-3">Grundlag ({list.length})</MLabel>}
        {list.length > 0 && <LineList testid={`${testid}-lines`}>{list.map((inp) => { const s = inputSummary(inp); const n = inp.type === "audit" && inp.ref?.id ? (findings || []).filter((f) => f.audit_id === inp.ref.id).length : 0; return (
          <LineRow key={inp.id} icon={INPUT_ICON[inp.type]} title={s.title} meta={[INPUT_LABEL[inp.type], s.meta, n ? `${n} fund` : null].filter(Boolean).join(" · ")} onOpen={() => setOpenInp(inp.id)} testid={`le-input-line-${inp.id}`}
            menu={!isFinal && [["Knyt til handling", () => onLinkAction(inp), "", `le-input-link-action-${inp.id}`], (inp.type === "audit" || inp.type === "reklamation") && ["Opdatér", () => onRefresh(inp), "", `le-input-refresh-${inp.id}`], ["Fjern", () => onRemove(inp), "text-[#A83232]", `le-input-remove-${inp.id}`]]} />); })}</LineList>}
        {autoSlot && <div className={list.length ? "mt-5" : ""}>{autoSlot}</div>}
      </div>}
      <SidePanel open={!!cur} onClose={() => setOpenInp(null)} title={cur ? inputSummary(cur).title : ""} sub={cur ? INPUT_LABEL[cur.type] : null} testid="le-input-panel">
        {cur && <>
          <InputCard input={cur} readonly={isFinal} onRefresh={() => onRefresh(cur)} onRemove={() => { onRemove(cur); setOpenInp(null); }} onLinkAction={() => onLinkAction(cur)} />
          {cur.type === "audit" && cur.ref?.id && (findings || []).some((f) => f.audit_id === cur.ref.id) && <div className="mt-3 space-y-2" data-testid={`le-input-findings-${cur.id}`}><MLabel className="mb-2">Fund fra auditten</MLabel>{findings.filter((f) => f.audit_id === cur.ref.id).map((f) => <FindingCard key={f.id} finding={f} readonly={isFinal} onChanged={onFindingsChanged} />)}</div>}
        </>}
      </SidePanel>
    </div>
  );
}

function AgendaContent({ item, pointNo, meeting, users, openActions, isFinal, snapshot, onUpdate, onRemoveItem, onAddInput, onRemoveInput, onRefreshInput, onUpdateChild, onAddChildInput, onRemoveChildInput, onRefreshChildInput, onCreateAction, onLinkAction, onActionsChanged, autoSlot, findings, findingsDue, onFindingsChanged }) {
  const special = item.special;
  const [forceOpen, setForceOpen] = useState(false);
  const pointActions = isFinal ? ((snapshot?.actions?.by_point || {})[String(pointNo)] || []) : (openActions || []).filter((a) => a.origin_meeting_id === meeting.id && a.origin?.point_no === pointNo);
  const seg = <HandlingSeg value={item.handling_status || null} note={item.handling_note} readonly={isFinal} onChange={(p) => onUpdate(p)} onNote={(v) => onUpdate({ handling_note: v })} testid={`le-handling-${item.id}`} />;

  if (special === "goals_review") return (
    <MPoint id={undefined} num={pointNo} title={item.title} testid={`le-agenda-content-${item.id}`}>
      <GoalsReview meeting={meeting} users={users} isFinal={isFinal} snapshot={snapshot} onActionsChanged={onActionsChanged} allActions={openActions} />
    </MPoint>
  );

  if (special === "actionplan_followup") {
    if (!isFinal) return <MPoint num={pointNo} title={item.title} testid={`le-agenda-content-${item.id}`}><ActionplanFollowup meeting={meeting} users={users} item={item} onActionsChanged={onActionsChanged} /></MPoint>;
    const snapActs = snapshot?.actions || {};
    return (
      <MPoint num={pointNo} title={item.title} meta={snapActs.since?.cutoff ? `Siden ${daDate(snapActs.since.cutoff)}` : null} testid={`le-agenda-content-${item.id}`}>
        {snapActs.since ? <SinceSection since={snapActs.since} readonly testid="le-snap-since" /> : <>
          {(snapActs.handled || []).length > 0 && <div className="space-y-3" data-testid="le-snap-actions-handled">{snapActs.handled.map((a, i) => <SnapActionCard key={i} a={a} showWork />)}</div>}
          {(snapActs.closed_since || []).length > 0 && <div className="space-y-3 mt-6" data-testid="le-closed-since-final"><MLabel className="mb-3">Lukket siden sidste møde ({snapActs.closed_since.length})</MLabel>{snapActs.closed_since.map((a, i) => <ClosedSinceCard key={i} a={a} />)}</div>}
        </>}
      </MPoint>
    );
  }

  if (special === "actionplan_next") {
    const acts = isFinal ? (snapshot?.actions?.open || []) : openActions;
    const rows = [...acts.map((a) => ({ id: a.id, no: a.action_no, title: a.description, status: a.status, overdue: a.overdue, deadline: a.deadline, newHere: a.origin_meeting_id === meeting.id })),
      ...(findingsDue || []).filter((f) => f.status !== "Lukket").map((f) => ({ id: f.id, no: f.finding_no, title: f.description, status: f.status, overdue: f.overdue, deadline: f.due_date }))];
    const seen = new Set(); const all = rows.filter((r) => !seen.has(r.id) && seen.add(r.id)).sort((x, y) => (x.deadline || "9999") < (y.deadline || "9999") ? -1 : 1);
    const nextDate = meeting.next_meeting_date; const limit = nextDate || toISO(addDays(toDate(meeting.date) || new Date(), 35));
    const over = all.filter((r) => r.overdue), soon = all.filter((r) => !r.overdue && r.deadline && r.deadline <= limit), later = all.filter((r) => !over.includes(r) && !soon.includes(r));
    const Rows = ({ rs }) => <MCompactRows rows={rs.map((r) => ({ ...r, status: r.newHere ? "Ny på mødet" : r.status }))} />;
    return (
      <MPoint num={pointNo} title={item.title} meta="Udfyldes automatisk" empty={all.length === 0} emptyText="ingen åbne handlinger eller fund" testid={`le-agenda-content-${item.id}`}>
        {over.length > 0 && <div data-testid="le-next-overdue"><MLabel red className="mb-3">Overskredet ({over.length})</MLabel><Rows rs={over} /></div>}
        {soon.length > 0 && <div className="mt-7" data-testid="le-next-soon"><MLabel className="mb-3">{nextDate ? "Frist inden næste møde" : "Frist inden for 5 uger"} ({soon.length})</MLabel><Rows rs={soon} /></div>}
        {later.length > 0 && <MCollapse label="Senere" count={later.length} testid="le-next-later"><Rows rs={later} /></MCollapse>}
      </MPoint>
    );
  }

  const hasText = !!(item.minute || (item.minute_blocks || []).length);
  const isUh = item.key === "uoenskede_haendelser";
  const openF = (findings || []).filter((f) => f.status !== "Lukket");
  const kids = item.children || [];
  const inlineEmpty = !forceOpen && !hasText && kids.length === 0 && (item.inputs || []).length === 0 && pointActions.length === 0 && !isUh && (item.handling_status === "udsat" || item.handling_status === "ikke_relevant" || isFinal);
  if (inlineEmpty) return (
    <section className="scroll-mt-24 mt-12 pt-8 border-t border-[#EBE8E3]" data-testid={`le-agenda-content-${item.id}`}>
      <div className="flex items-center gap-3 flex-wrap">
        <span className="font-mono text-sm text-stone-400">{pointNo}</span><h2 className="text-xl md:text-2xl font-semibold tracking-tight text-stone-500">{item.title}</h2>
        {item.handling_status && <span className={`text-[11px] px-2.5 py-1 rounded-full ${HANDLING_BADGE[item.handling_status]}`}>{HANDLING_OPTS.find((o) => o[0] === item.handling_status)?.[1]}</span>}
        <span className="text-sm text-stone-400">{item.handling_note || "– intet til dette punkt"}</span>
        {!isFinal && <button onClick={() => setForceOpen(true)} className="ml-auto text-sm text-stone-600 hover:text-stone-900 underline-offset-2 hover:underline" data-testid={`le-open-point-${item.id}`}>Åbn punkt</button>}
      </div>
    </section>
  );
  const kidsMeta = kids.length ? `${kids.filter((c) => c.handling_status).length} af ${kids.length} behandlet` : null;
  return (
    <MPoint num={pointNo} title={!isFinal && item.kind === "extra" ? <input value={item.title} onChange={(e) => onUpdate({ title: e.target.value })} className="bg-transparent outline-none border-b border-transparent focus:border-[#EBE8E3]" data-testid="le-item-title-edit" placeholder="Punkt-titel" /> : item.title} meta={kids.length ? kidsMeta : seg} testid={`le-agenda-content-${item.id}`}>
      {item.owner_label && <p className="text-sm text-[#806B61] -mt-2 mb-3">Ansvarlig for punktet: {item.owner_label}</p>}
      {!isFinal && item.kind === "extra" && <button onClick={onRemoveItem} className="text-xs text-[#A83232] hover:underline mb-2">Fjern punkt</button>}
      {kids.length > 0 ? (
        <div className="border-l-2 border-[#EBE8E3] pl-5 space-y-8" data-testid={`le-children-${item.id}`}>
          {kids.map((ch, ci) => (
            <div key={ch.id} data-testid={`le-child-${ch.id}`}>
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <p className="text-[17px] font-semibold text-[#1A1919]">{pointNo}.{ci + 1} {ch.title}{ch.owner_label && <span className="font-normal text-sm text-stone-500"> · {ch.owner_label}</span>}</p>
                <HandlingSeg value={ch.handling_status || null} note={ch.handling_note} readonly={isFinal} onChange={(p) => onUpdateChild(ch.id, p)} onNote={(v) => onUpdateChild(ch.id, { handling_note: v })} testid={`le-child-handling-${ch.id}`} />
              </div>
              {ch.help && !isFinal && <p className="text-sm text-stone-400 mt-1">{ch.help}</p>}
              <div className="mt-3">{isFinal ? <MinuteBlocksView blocks={ch.minute_blocks} minute={ch.minute} empty="Ingen referattekst." textClass="text-[17px] leading-relaxed text-[#1A1919]" tableCompact /> : <MinuteBlocksEditor blocks={ch.minute_blocks} minute={ch.minute} onChange={(mb, text) => onUpdateChild(ch.id, { minute_blocks: mb, minute: text })} testid={`le-child-minute-${ch.id}`} />}</div>
              <Grundlag inputs={ch.inputs} isFinal={isFinal} meeting={meeting} onAdd={(inp) => onAddChildInput(ch.id, inp)} onRemove={(inp) => onRemoveChildInput(ch.id, inp)} onRefresh={(inp) => onRefreshChildInput(ch.id, inp)} onLinkAction={onLinkAction} onCreateAction={() => onCreateAction(null, ch)} findings={findings} onFindingsChanged={onFindingsChanged} testid={`le-section-grundlag-${ch.id}`} />
            </div>
          ))}
        </div>
      ) : (<>
        {isFinal ? <MinuteBlocksView blocks={item.minute_blocks} minute={item.minute} empty="Ingen bemærkninger" textClass="text-[17px] leading-relaxed text-[#1A1919]" /> : <MinuteBlocksEditor blocks={item.minute_blocks} minute={item.minute} onChange={(mb, text) => onUpdate({ minute_blocks: mb, minute: text })} testid="le-minute" />}
        {pointActions.length > 0 && <div className="mt-6" data-testid={`le-point-actions-${item.id}`}><MLabel className="mb-3">Handlinger besluttet her</MLabel><div className="space-y-2">{pointActions.map((a) => isFinal ? <FollowupCardCompact key={a.id} action={a} /> : <FollowupCard key={a.id} action={a} users={users} meeting={meeting} onChanged={onActionsChanged} hideLatest />)}</div></div>}
        {isUh && openF.length > 0 && <div className="mt-6" data-testid={`le-section-fund-${item.id}`}><MLabel className="mb-3">Åbne fund ({openF.length})</MLabel><div className="space-y-3">{openF.map((f) => <FindingCard key={f.id} finding={f} readonly={isFinal} onChanged={onFindingsChanged} />)}</div></div>}
        <Grundlag inputs={item.inputs} isFinal={isFinal} meeting={meeting} onAdd={onAddInput} onRemove={onRemoveInput} onRefresh={onRefreshInput} onLinkAction={onLinkAction} onCreateAction={onCreateAction} findings={findings} onFindingsChanged={onFindingsChanged} testid={`le-section-grundlag-${item.id}`} autoSlot={autoSlot} />
      </>)}
    </MPoint>
  );
}
